import { useEffect, useState, type FC, type PropsWithChildren } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { Flex, Text } from '@eqi-investimentos/react';
import { useT } from '@/libs/i18n/utils/useT';
import { OtpCodeInput } from '@/components/sections/OtpCodeInput';
import { EDIT_PROFILE_INFO_DRAWER_IDS } from '../utils';

export const OTPConfirmation: FC<OTPConfirmationProps> = ({
  children,
  isNewValueSet,
  type,
  isError = false,
  isLoading = false,
  handleOTPValidation,
  resendOTP,
}) => {
  const t = useT();
  const { data: session } = useSession();

  const [hasOTPError, setHasOTPError] = useState(isError);
  const [isValidatingOTP, setIsValidatingOTP] = useState(false);
  const [isOTPValid, setIsOTPValid] = useState(false);

  const validateOTP = async (token: string) => {
    setHasOTPError(false);

    if (token.length < 6) return;

    setIsValidatingOTP(true);

    const response = await signIn('otp', {
      identifier: session!.user.document || session!.user.email,
      token,
      redirect: false,
    });

    if (!response || response.error) return setHasOTPError(true);

    setIsOTPValid(true);
  };

  useEffect(() => {
    setHasOTPError(isError);
  }, [isError]);

  if (type === 'name' || !isNewValueSet || isOTPValid) {
    return children;
  }

  const replaceType = {
    password: 'e-mail',
    email: 'novo e-mail',
    phone: 'novo telefone',
  }[type];

  return (
    <Flex flexDir="column" gap="m">
      <Text size="h6" fontWeight="medium">
        {t('drawers.personalInfo.otpConfirmation.header')}
      </Text>
      <Text size="xl">
        {t('drawers.personalInfo.otpConfirmation.description').replace(
          '<type>',
          replaceType
        )}
      </Text>
      <OtpCodeInput
        isDisabled={isLoading}
        error={hasOTPError ? t('screens.otp.errorCodeValidation') : ''}
        loading={isValidatingOTP}
        otpValidationTime={300}
        handleCode={handleOTPValidation || validateOTP}
        resendSendOtp={resendOTP}
      />
    </Flex>
  );
};

export type OTPConfirmationProps = PropsWithChildren<{
  isNewValueSet: boolean;
  type: keyof typeof EDIT_PROFILE_INFO_DRAWER_IDS;
  isLoading?: boolean;
  isError?: boolean;
  resendOTP: () => void;
  handleOTPValidation?: (token: string) => void | Promise<void>;
}>;
